/**
 * InvoiceComparisonDialog - Dialog for comparing invoices
 *
 * Displays invoice comparison data in two tabs:
 * - Table: Formatted table of invoice amounts
 * - Chart: Bar chart (n=1) or line chart (n>1) visualization
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { XIcon } from '@phosphor-icons/react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency } from '@erp/shared-utils';

export interface InvoiceData {
  id: string;
  invoiceNumber: string;
  invoiceDate: string; // "YYYY-MM-DD"
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
}

export interface ComparisonData {
  currentInvoice: InvoiceData;
  previousInvoices: InvoiceData[];
  supplierName: string;
  currency: string;
  requestedN: number;
  actualN: number;
}

export interface ChartData {
  id: string;
  title: string;
  description: string;
  chartType: 'bar' | 'line';
  data: Array<Record<string, unknown>>;
  xAxisLabel: string;
  yAxisLabel: string;
  color: string;
  secondaryColor?: string;
}

interface InvoiceComparisonDialogProps {
  open: boolean;
  onClose: () => void;
  comparisonData: ComparisonData | null;
  chart: ChartData | null;
}

// Custom tooltip formatter for currency values
const currencyFormatter = (value: number) => formatCurrency(value);

export function InvoiceComparisonDialog({
  open,
  onClose,
  comparisonData,
  chart,
}: InvoiceComparisonDialogProps) {
  if (!comparisonData || !chart) return null;

  const renderBarChart = () => {
    // Detect data keys (excluding 'name')
    const dataKeys = Object.keys(chart.data[0] || {}).filter(
      (key) => key !== 'name',
    );

    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chart.data}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11 }}
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
            label={{
              value: 'Invoice Date',
              position: 'insideBottomRight',
              offset: -10,
            }}
          />
          <YAxis
            tickFormatter={currencyFormatter}
            tick={{ fontSize: 11 }}
            label={{
              value: chart.yAxisLabel,
              angle: -90,
              position: 'left',
              offset: 10,
              style: { textAnchor: 'middle', fontSize: 12 },
            }}
          />
          <Tooltip
            formatter={(value) => [
              typeof value === 'number' ? formatCurrency(value) : String(value),
              '',
            ]}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
          />
          <Legend />
          {dataKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              name={formatKeyName(key)}
              fill={
                index === 0 ? chart.color : chart.secondaryColor || chart.color
              }
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderLineChart = () => {
    // Detect data keys (excluding 'name')
    const dataKeys = Object.keys(chart.data[0] || {}).filter(
      (key) => key !== 'name',
    );

    return (
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chart.data}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12 }}
            label={{
              value: 'Invoice Date',
              position: 'insideBottomRight',
              offset: -10,
            }}
          />
          <YAxis
            tickFormatter={currencyFormatter}
            tick={{ fontSize: 11 }}
            label={{
              value: chart.yAxisLabel,
              angle: -90,
              position: 'left',
              offset: 10,
              style: { textAnchor: 'middle', fontSize: 12 },
            }}
          />
          <Tooltip
            formatter={(value) => [
              typeof value === 'number' ? formatCurrency(value) : 'N/A',
              '',
            ]}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
          />
          <Legend />
          {dataKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              name={formatKeyName(key)}
              stroke={
                index === 0 ? chart.color : chart.secondaryColor || chart.color
              }
              strokeWidth={2}
              dot={{ r: 4 }}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderChart = () => {
    switch (chart.chartType) {
      case 'bar':
        return renderBarChart();
      case 'line':
        return renderLineChart();
      default:
        return <div>Unsupported chart type</div>;
    }
  };

  // Helper to format camelCase keys to readable labels
  const formatKeyName = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase());
  };

  // Calculate average total for n>1 comparison
  const allInvoices = [
    comparisonData.currentInvoice,
    ...comparisonData.previousInvoices,
  ];
  const averageTotal =
    allInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0) /
    allInvoices.length;

  const renderTableTab = () => {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left px-4 py-2 font-semibold">Invoice</th>
              <th className="text-left px-4 py-2 font-semibold">Date</th>
              <th className="text-right px-4 py-2 font-semibold">Subtotal</th>
              <th className="text-right px-4 py-2 font-semibold">Tax</th>
              <th className="text-right px-4 py-2 font-semibold">Total</th>
              <th className="text-right px-4 py-2 font-semibold">Delta</th>
            </tr>
          </thead>
          <tbody>
            {/* Current invoice - highlighted */}
            <tr className="border-b border-gray-100 bg-blue-50">
              <td className="px-4 py-2 font-medium">
                {comparisonData.currentInvoice.invoiceNumber}
              </td>
              <td className="px-4 py-2">
                {comparisonData.currentInvoice.invoiceDate}
              </td>
              <td className="text-right px-4 py-2">
                {formatCurrency(comparisonData.currentInvoice.subtotal)}
              </td>
              <td className="text-right px-4 py-2">
                {formatCurrency(comparisonData.currentInvoice.taxAmount)}
              </td>
              <td className="text-right px-4 py-2 font-medium">
                {formatCurrency(comparisonData.currentInvoice.totalAmount)}
              </td>
              <td className="text-right px-4 py-2 text-gray-500">—</td>
            </tr>

            {/* Previous invoices */}
            {comparisonData.previousInvoices.map((invoice) => {
              const delta =
                comparisonData.currentInvoice.totalAmount - invoice.totalAmount;
              const deltaPercent =
                invoice.totalAmount > 0
                  ? ((delta / invoice.totalAmount) * 100).toFixed(1)
                  : 0;
              const deltaColor = delta > 0 ? 'text-red-600' : 'text-green-600';

              return (
                <tr key={invoice.id} className="border-b border-gray-100">
                  <td className="px-4 py-2">{invoice.invoiceNumber}</td>
                  <td className="px-4 py-2">{invoice.invoiceDate}</td>
                  <td className="text-right px-4 py-2">
                    {formatCurrency(invoice.subtotal)}
                  </td>
                  <td className="text-right px-4 py-2">
                    {formatCurrency(invoice.taxAmount)}
                  </td>
                  <td className="text-right px-4 py-2">
                    {formatCurrency(invoice.totalAmount)}
                  </td>
                  <td className={`text-right px-4 py-2 ${deltaColor}`}>
                    {formatCurrency(delta)} ({deltaPercent}%)
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Invoice Comparison - {comparisonData.supplierName}
          </DialogTitle>
          <DialogDescription>
            Comparing {comparisonData.actualN} previous invoice
            {comparisonData.actualN !== 1 ? 's' : ''} in{' '}
            {comparisonData.currency}
            {comparisonData.actualN < comparisonData.requestedN && (
              <span className="block text-xs text-amber-600 mt-1">
                Only {comparisonData.actualN} previous invoice
                {comparisonData.actualN !== 1 ? 's' : ''} available (requested{' '}
                {comparisonData.requestedN})
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="table" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="table">Table</TabsTrigger>
            <TabsTrigger value="chart">Chart</TabsTrigger>
          </TabsList>

          <TabsContent value="table" className="py-4">
            {renderTableTab()}
          </TabsContent>

          <TabsContent value="chart" className="py-4">
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-medium">{chart.title}</h3>
              <p className="text-xs text-gray-600">{chart.description}</p>
              <div className="mt-4">{renderChart()}</div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
          >
            <XIcon className="h-4 w-4" />
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default InvoiceComparisonDialog;
